import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import AuthImage from "./AuthImage";
import EmbeddedQuestion from "./EmbeddedQuestion";
import { toast } from "sonner";
import {
  Loader2, X, Camera, CheckCircle2, Clock, Eye, EyeOff, Save, Sparkles,
  Calculator as CalcIcon, KeyRound, RefreshCw,
} from "lucide-react";

/**
 * One child's attempt at one worksheet, for the tutor to read and mark.
 *
 * This is the screen the portal did not have. A hand-in was visible as a
 * number - "3 handed in" - and the working the child had photographed could
 * not be opened from anywhere. A tutor could set work and could not mark it.
 *
 * The question itself is framed from the bank, as it is for the student, so
 * what the tutor marks against is exactly what the child was shown. Closed by
 * default: a tutor marking twenty of these knows the paper, and twenty frames
 * loading at once is a slow screen.
 *
 * The mark box is bounded by the question's own marks. A tutor typing 5 into
 * a 4-mark question produces a percentage over 100, and no page further down
 * would notice.
 */

function Lightbox({ path, onClose }) {
  useEffect(() => {
    const esc = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-[60] bg-slate-900/90 flex items-center justify-center p-4" onClick={onClose}>
      <button type="button" onClick={onClose} className="absolute top-3 right-3 text-white/80 hover:text-white p-2">
        <X size={22} />
      </button>
      <AuthImage path={path} alt="The student's working, full size"
        className="max-h-full max-w-full rounded-lg bg-white" />
    </div>
  );
}

function QuestionRow({
  code, index, meta, answers, photos, selfMark, tutorMark, comment,
  keys, checked, tape, onSave,
}) {
  const available = meta?.marks;
  // Prefilled with the automatic result where there is one and the tutor
  // has not marked it yet, so agreeing with the machine is one click. It is
  // a suggestion in an editable box, never a stored mark - nothing writes a
  // mark on a child's behalf.
  const suggested = checked.marks != null && checked.possible > 0 ? checked.marks : "";
  const [marks, setMarks] = useState(tutorMark ?? suggested);
  const [note, setNote] = useState(comment || "");
  const [saving, setSaving] = useState(false);
  const [showQuestion, setShowQuestion] = useState(false);
  const [big, setBig] = useState(null);

  useEffect(() => { setMarks(tutorMark ?? suggested); setNote(comment || ""); }, [tutorMark, comment, suggested]);

  const save = () => {
    const n = Number(marks);
    if (marks === "" || Number.isNaN(n)) { toast.error("Type a number of marks"); return; }
    if (available != null && n > available) { toast.error(`${code} is worth ${available} marks`); return; }
    setSaving(true);
    onSave(code, n, note).finally(() => setSaving(false));
  };

  const dirty = String(marks) !== String(tutorMark ?? "") || note !== (comment || "");

  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden">
      <div className="px-3.5 py-2.5 bg-slate-50 border-b border-slate-200 flex flex-wrap items-center gap-x-3 gap-y-1.5">
        <span className="text-xs text-slate-400 font-semibold">{index + 1}</span>
        <span className="font-mono text-sm font-semibold">{code}</span>
        {available != null && <span className="text-xs text-slate-500">out of {available}</span>}
        {meta?.topic && <span className="text-xs text-slate-400 truncate max-w-[200px]">{meta.topic}</span>}
        <span className={`text-xs inline-flex items-center gap-1 ${photos.length ? "text-green-700" : "text-slate-400"}`}>
          <Camera size={12} /> {photos.length}
        </span>
        <button type="button" onClick={() => setShowQuestion((v) => !v)}
          className="ml-auto text-xs inline-flex items-center gap-1 text-slate-500 hover:text-slate-800">
          {showQuestion ? <EyeOff size={12} /> : <Eye size={12} />}
          {showQuestion ? "Hide the question" : "Show the question"}
        </button>
      </div>

      <div className="p-3.5 space-y-3">
        {showQuestion && <EmbeddedQuestion code={code} />}

{/* One line per part, with what the paper expects beside what the child
    wrote. A tutor marking by hand wants the key even where the checker
    refused to use it - and especially then, since the reason says why. */}
        <div className="rounded-lg border border-slate-200 overflow-hidden">
          {(keys.length ? keys : [{ part_label: null }]).map((k, n) => {
            const part = k.part_label || "";
            const field = part ? `${code}|${part}` : code;
            const typed = (answers || {})[field];
            const verdict = (checked.parts || {})[part];
            return (
              <div key={n} className="px-3 py-2 border-b border-slate-100 last:border-b-0 flex flex-wrap items-baseline gap-x-3 gap-y-1 text-sm">
                {part && <span className="font-semibold text-slate-700 w-6">({part})</span>}
                <span className="font-mono">
                  {typed ? typed : <span className="text-slate-400">nothing typed</span>}
                </span>
                {k.raw_answer ? (
                  <span className="text-xs text-slate-500 inline-flex items-center gap-1">
                    <KeyRound size={11} className="text-slate-400" />
                    paper says <strong className="font-mono text-slate-700">{k.raw_answer}</strong>
                    {k.marks != null ? ` (${k.marks}m)` : ""}
                  </span>
                ) : null}
                {verdict === "correct" && (
                  <span className="text-xs font-semibold text-green-700 inline-flex items-center gap-1">
                    <Sparkles size={11} /> matched
                  </span>
                )}
                {verdict === "unmatched" && (
                  <span className="text-xs text-amber-700">did not match</span>
                )}
                {verdict === "not-checked" && k.reason && (
                  <span className="text-xs text-slate-400" title={k.reason}>not checkable &mdash; {k.reason}</span>
                )}
              </div>
            );
          })}
        </div>

        {selfMark != null && (
          <p className="text-xs text-slate-500">
            They gave themselves <strong className="text-slate-700">{selfMark}</strong>
            {available != null ? ` of ${available}` : ""}.
          </p>
        )}

        {tape && tape.length > 0 && (
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
            <p className="text-[11px] uppercase tracking-wider font-bold text-slate-400 mb-1 inline-flex items-center gap-1">
              <CalcIcon size={11} /> What they keyed into the calculator
            </p>
            {tape.map((line, n) => (
              <div key={n} className="font-mono text-xs text-slate-600">{line}</div>
            ))}
          </div>
        )}

        {photos.length > 0 ? (
          <div>
            <p className="text-xs uppercase tracking-wider font-bold text-slate-400 mb-1.5">Their working</p>
            <div className="flex flex-wrap gap-2">
              {photos.map((p) => (
                <button key={p.file_id} type="button" onClick={() => setBig(`/working/${p.file_id}`)}
                  className="w-[108px] h-[108px] rounded-lg overflow-hidden border border-slate-300 bg-white">
                  <AuthImage path={`/working/${p.file_id}`} alt={`Working for ${code}`}
                    className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>
        ) : (
          <p className="text-xs text-slate-400">No photo of working for this question.</p>
        )}

        <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-slate-100">
          <label className="text-xs text-slate-600 font-semibold" htmlFor={`m-${code}`}>Your mark</label>
          <input id={`m-${code}`} type="number" min={0} max={available ?? undefined}
            value={marks} onChange={(e) => setMarks(e.target.value)}
            className="w-[70px] text-sm border border-slate-300 rounded-lg px-2 py-1.5" />
          {available != null && <span className="text-xs text-slate-400">/ {available}</span>}
          <input value={note} onChange={(e) => setNote(e.target.value.slice(0, 500))}
            placeholder="a comment for them (optional)"
            className="flex-1 min-w-[160px] text-sm border border-slate-300 rounded-lg px-2.5 py-1.5" />
          <button type="button" onClick={save} disabled={saving || !dirty}
            className="inline-flex items-center gap-1.5 text-sm font-semibold bg-slate-800 text-white rounded-lg px-3 py-1.5 disabled:opacity-40">
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
            {tutorMark != null && !dirty ? "Saved" : "Save"}
          </button>
        </div>
      </div>

      {big && <Lightbox path={big} onClose={() => setBig(null)} />}
    </div>
  );
}

export default function AttemptReview({ attemptId, onClose, onChanged }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);

  const load = useCallback(() => {
    api.get(`/admin/attempts/${attemptId}`)
      .then((r) => setD(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Could not open that attempt"));
  }, [attemptId]);

  useEffect(() => { load(); }, [load]);

  const saveMark = (code, marks, comment) =>
    api.post(`/admin/attempts/${attemptId}/mark`, { question_code: code, marks, comment })
      .then(() => { toast.success(`${code} marked`); load(); onChanged && onChanged(); })
      .catch((e) => { toast.error(e.response?.data?.detail || "Could not save that mark"); });

  const att = d?.attempt;
  const a = d?.assignment;
  const meta = d?.meta || {};
  const codes = a?.question_codes || [];
  const byCode = {};
  for (const w of att?.working || []) {
    (byCode[w.question_code] = byCode[w.question_code] || []).push(w);
  }

  const autoDetail = att?.auto_detail || {};

  let awarded = 0;
  let available = 0;
  let marked = 0;
  let confirmed = 0;
  for (const c of codes) {
    const m = (att?.tutor_marks || {})[c];
    const out = meta[c]?.marks;
    if (m != null && out != null) { awarded += m; available += out; marked += 1; }
    else confirmed += (autoDetail[c] || {}).marks || 0;
  }

  const recheck = () =>
    api.post(`/admin/attempts/${attemptId}/auto-check`)
      .then(() => { toast.success("Answers checked"); load(); })
      .catch((e) => toast.error(e.response?.data?.detail || "Could not check them"));

  /** Add time, or start the clock again. The answer to "the wifi dropped". */
  const adjustTime = (action, minutes) =>
    api.post(`/admin/attempts/${attemptId}/time`, { action, minutes })
      .then(() => {
        toast.success(action === "add" ? `${minutes} minutes added` : "Clock restarted");
        load(); onChanged && onChanged();
      })
      .catch((e) => toast.error(e.response?.data?.detail || "Could not change the clock"));

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-start justify-center overflow-y-auto p-4">
      <div className="bg-white rounded-xl w-full max-w-3xl my-6 shadow-xl">
        <div className="flex items-center justify-between gap-3 px-5 py-3.5 border-b border-slate-200">
          <div className="min-w-0">
            <h2 className="font-semibold text-slate-800 truncate">
              {d?.student?.name || "Student"} — {a?.title || "Attempt"}
            </h2>
            {att && (
              <p className="text-xs text-slate-500 mt-0.5">
                {att.status === "submitted"
                  ? <span className="inline-flex items-center gap-1 text-green-700"><CheckCircle2 size={11} /> handed in{att.submitted_at ? ` ${new Date(att.submitted_at).toLocaleDateString("en-GB")}` : ""}</span>
                  : <span className="inline-flex items-center gap-1 text-amber-700"><Clock size={11} /> not handed in yet</span>}
                {" · "}{marked} of {codes.length} marked by you
                {available > 0 ? ` · ${awarded} of ${available} marks (${Math.round((awarded / available) * 100)}%)` : ""}
                {confirmed > 0 ? ` · ${confirmed} more confirmed automatically` : ""}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={recheck} title="Check the typed answers against the mark scheme again"
              className="text-xs inline-flex items-center gap-1 border border-slate-300 rounded-lg px-2.5 py-1.5 hover:bg-slate-50">
              <RefreshCw size={12} /> Check answers
            </button>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
          </div>
        </div>

        <div className="p-5">
          {err && <p className="text-sm text-red-700">{err}</p>}
          {!d && !err && (
            <p className="flex items-center gap-2 text-slate-500 text-sm">
              <Loader2 size={15} className="animate-spin" /> Loading…
            </p>
          )}
          {d && att.status !== "submitted" && (
            <p className="mb-4 text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3.5 py-2.5">
              This has not been handed in. You can still read what is there and mark it, but the child can
              change their answers until they do.
            </p>
          )}

          {/* The clock. Shown only for a timed paper, because there is nothing
              to adjust on a self-paced worksheet. */}
          {d && att.timing?.timed && (
            <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 px-3.5 py-3">
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
                <p className="text-sm font-semibold text-slate-800 inline-flex items-center gap-1.5">
                  <Clock size={14} className="text-slate-400" />
                  {!att.timing.started
                    ? `${Math.round((att.timing.limit_seconds || 0) / 60)} minute paper — not started`
                    : att.timing.expired
                      ? "Time is up"
                      : `${Math.floor(att.timing.seconds_remaining / 60)}m ${att.timing.seconds_remaining % 60}s left`}
                </p>
                {att.timing.extensions_used > 0 && (
                  <span className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                    {att.timing.extensions_used} extra upload{att.timing.extensions_used === 1 ? "" : "s"}
                  </span>
                )}
                {att.seconds_on_screen > 0 && (
                  <span className="text-xs text-slate-500">
                    {Math.round(att.seconds_on_screen / 60)} min on screen
                  </span>
                )}
                {att.status !== "submitted" && (
                  <span className="ml-auto flex flex-wrap items-center gap-1.5">
                    {[5, 10, 20].map((n) => (
                      <button key={n} onClick={() => adjustTime("add", n)}
                        className="text-xs border border-slate-300 bg-white rounded-lg px-2 py-1 hover:bg-slate-100">
                        +{n} min
                      </button>
                    ))}
                    <button onClick={() => adjustTime("reset")}
                      title="Give the whole time again, from now"
                      className="text-xs border border-slate-300 bg-white rounded-lg px-2 py-1 hover:bg-slate-100">
                      Restart clock
                    </button>
                  </span>
                )}
              </div>
              {(att.time_events || []).length > 0 && (
                <ul className="mt-2 text-xs text-slate-500 space-y-0.5">
                  {att.time_events.map((e, n) => (
                    <li key={n}>
                      {e.action === "add" ? `+${e.minutes} minutes` : e.action === "reset" ? "clock restarted" : "deadline set"}
                      {e.at ? ` · ${new Date(e.at).toLocaleString("en-GB")}` : ""}
                      {e.note ? ` · ${e.note}` : ""}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          {d && (
            <div className="space-y-3">
              {codes.map((code, i) => (
                <QuestionRow
                  key={code}
                  code={code}
                  index={i}
                  meta={meta[code]}
                  answers={att.answers || {}}
                  keys={(d.keys || {})[code] || []}
                  checked={autoDetail[code] || {}}
                  tape={(att.calc_tape || {})[code] || []}
                  photos={byCode[code] || []}
                  selfMark={(att.self_marks || {})[code]}
                  tutorMark={(att.tutor_marks || {})[code]}
                  comment={(att.tutor_comments || {})[code]}
                  onSave={saveMark}
                />
              ))}
              {codes.length === 0 && <p className="text-sm text-slate-500">This worksheet has no questions on it.</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
